// todo translate
import React, { useCallback, useContext } from "react";
import differenceInDays from "date-fns/differenceInDays";

import { Box, darken, Grid, lighten, Tooltip, Typography } from "@mui/material";

import AttachedDocuments from "../../AttachedDocuments/AttachedDocuments";
import Deadline from "./Deadline/Deadline";
import DocReportCellActions from "./DocReportCellActions/DocReportCellActions";

import { Period, PeriodType } from "../../../models/documents/period";

import { hasAllProcessedDocuments, hasAllVerifiedDocuments } from "./utils";

import { Props } from "./interfaces";
import {
  DeadlineAndActions,
  TitleName,
  MainColumnBlock,
  CellBlock,
  CategoryNameWrapper,
  CategoryNameContainer,
} from "./styles";
import { useTheme } from "@mui/material";
import { ALL_LEGISLATIONS_ID } from "../../../utils/constants/constants";
import DocTypeActions from "./DocTypeActions/DocTypeActions";
import { DocumentsReportPeriodDTO } from "../../../models/documents/documents-reports/documents-report-period-dto";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { DocumentRef } from "../../../models/documents";
import { MyTeamPayrollOutputDocumentsContext } from "../../../views/documents-reports/MyTeamPayrollOutputReport/MyTeamPayrollOutputReport";
import { EmployeeGroupRef } from "../../../models/employee-groups/employee-group-ref";
import { DocumentsReportCategoryGroupDTO, InvoiceBalance } from "src/models/documents/documents-reports";
import { ScreenReport } from "../interfaces";
import { COLORS } from "src/_globalStore/redux/reducers/Theme/themeConstants";
import { PERIOD_CELL_ROW, TABLE_BODY_ROW, TABLE_CELL } from "../DocumentsPeriod/utils";
import { DocumentsReportPeriodAndCurrencyDTO } from "src/models/documents/documents-reports/documents-report-period-and-currency-dto";

const DocumentsReportCategory = ({
  reportCategoryGroups,
  categoryName,
  categoryCode,
  handleNotifyTable,
  legalEntity,
  reportYear,
  askAction,
  askHide = false,
  askVerified = false,
  dynamicColumn,
  screen,
  AmountDetails,
  itemIdx,
  topForStickyRows,
}: Props) => {
  const theme = useTheme();
  const { userIsClient } = useContext(MyTeamPayrollOutputDocumentsContext);

  const TODAY = new Date();
  const getReportCategoryGroupDocIDs = (reportCategoryDocsByPeriod: DocumentsReportPeriodDTO[] | null): string[] => {
    let docIDs: string[] = [];
    reportCategoryDocsByPeriod?.forEach((docsByPeriod) => {
      docsByPeriod?.documents?.forEach((d) => docIDs.push(d.id));
    });

    return docIDs;
  };

  const askForAction = (
    action: "markAllVerified" | "hideLine",
    reportCategoryDocsByPeriod: DocumentsReportPeriodDTO[] | null,
    docTypeId: string
  ) => {
    if (!!askAction && !!reportCategoryDocsByPeriod) {
      let docIDs = getReportCategoryGroupDocIDs(reportCategoryDocsByPeriod);
      askAction(action, docIDs, docTypeId);
    }
  };
  const getNumberOfDocsWithoutLegalEntity = useCallback((documents: DocumentRef[]): number => {
    return documents?.filter((doc) => !doc?.legalEntity).length ?? 0;
  }, []);

  return (
    <>
      {userIsClient ? (
        <>
          <Grid
            className={TABLE_BODY_ROW}
            sx={{
              width: "100%",
              backgroundColor: lighten(theme.palette.secondary.light, 0.8),
              position: "relative",
              zIndex: itemIdx + 5,
            }}
          >
            <Grid sx={{ padding: "16px", display: "flex", flexDirection: "row", gap: "10px", alignItems: "center" }}>
              <TitleName
                variant="h6"
                title={`${categoryName} (${categoryCode})`}
              >
                {categoryName}
              </TitleName>
              {!reportCategoryGroups?.length && (
                <Typography sx={{ fontStyle: "italic" }}>
                  (No documents available for this document category)
                </Typography>
              )}
            </Grid>
          </Grid>

          {reportCategoryGroups.map((reportCategory, idx) => {
            const removeDuplicates = (arr: any, prop: string) => {
              return arr.filter(
                (item: any, index: number, self: any) =>
                  index === self.findIndex((obj: any) => obj?.[prop] === item?.[prop])
              );
            };

            const employeeGroups =
              reportCategory?.docsByPeriod
                ?.map((docPeriod) => docPeriod?.documents?.map((document) => document?.employeeGroup))
                ?.reduce((acc, curr) => {
                  return [...(acc ?? []), ...(curr ?? [])];
                }, []) ?? [];

            const filteredEmployeeGroups = removeDuplicates(employeeGroups, "id");

            return (
              <React.Fragment key={reportCategory.id}>
                <Grid
                  className={TABLE_BODY_ROW}
                  sx={{
                    width: "100%",
                    position: "sticky",
                    top: topForStickyRows,
                    background: "white",
                    zIndex: (itemIdx + 5) * (idx + 1),
                  }}
                >
                  {!!reportCategory?.docsByPeriod?.length ? (
                    <Grid
                      className={TABLE_BODY_ROW}
                      container
                      wrap="nowrap"
                      sx={{
                        width: "100%",

                        [`& .${TABLE_CELL}`]: {
                          borderRight: `1px solid ${darken(COLORS.black_gradients.greyLight, 0.2)}`,
                        },

                        [`& .${PERIOD_CELL_ROW}`]: {
                          "& div:last-of-type": {
                            borderRight: `0px`,
                          },
                        },
                      }}
                    >
                      <Grid
                        item
                        sx={{ width: "20%", minWidth: "20%", flexGrow: 1 }}
                        className={TABLE_CELL}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            height: "100%",
                            padding: "16px",
                          }}
                        >
                          <TitleName title={`Documents type: ${reportCategory.reportGroupCode}`}>
                            {reportCategory.reportGroupName +
                              (reportCategory.docType &&
                              reportCategory.docType?.legislation &&
                              reportCategory.docType?.legislation?.id !== ALL_LEGISLATIONS_ID
                                ? " (" + reportCategory.docType?.legislation?.code + ")"
                                : "")}
                          </TitleName>
                        </Box>
                      </Grid>

                      <Grid
                        item
                        container
                        wrap="nowrap"
                        sx={{
                          flexGrow: reportCategory?.docsByPeriod?.length,
                          width: "100%",
                          justifyContent: "flex-start",
                          height: "100%",
                          padding: "16px",
                        }}
                        className={PERIOD_CELL_ROW}
                      >
                        <Grid
                          item
                          xs
                          sx={{ flexGrow: 1 }}
                          className={TABLE_CELL}
                        >
                          {!filteredEmployeeGroups?.length && (
                            <Typography sx={{ fontStyle: "italic" }}>
                              (No documents available for this document type)
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid
                      sx={{ padding: "16px", display: "flex", flexDirection: "row", gap: "10px", alignItems: "center" }}
                    >
                      <TitleName
                        title={`Documents type: ${reportCategory.reportGroupCode}`}
                        sx={{ width: "20%" }}
                      >
                        {reportCategory.reportGroupName +
                          (reportCategory.docType &&
                          reportCategory.docType?.legislation &&
                          reportCategory.docType?.legislation?.id !== ALL_LEGISLATIONS_ID
                            ? " (" + reportCategory.docType?.legislation?.code + ")"
                            : "")}
                      </TitleName>

                      {!filteredEmployeeGroups?.length && (
                        <Typography sx={{ fontStyle: "italic" }}>
                          (No documents available for this document type)
                        </Typography>
                      )}
                    </Grid>
                  )}
                </Grid>

                {filteredEmployeeGroups.map((group: EmployeeGroupRef) => {
                  const groupsDocsByPeriod = reportCategory?.docsByPeriod?.map((period) => {
                    return {
                      ...period,
                      documents: period?.documents?.filter((doc) => doc?.employeeGroup?.id === group?.id) ?? [],
                    };
                  });

                  return (
                    <>
                      {!!groupsDocsByPeriod?.length && (
                        <>
                          <Grid
                            className={TABLE_BODY_ROW}
                            container
                            wrap="nowrap"
                            sx={{
                              width: "100%",

                              [`& .${TABLE_CELL}`]: {
                                borderRight: `1px solid ${darken(COLORS.black_gradients.greyLight, 0.2)}`,
                              },

                              [`& .${PERIOD_CELL_ROW}`]: {
                                "& div:last-of-type": {
                                  borderRight: `0px`,
                                },
                              },
                            }}
                          >
                            <Grid
                              item
                              sx={{ width: "20%", minWidth: "20%", flexGrow: 1 }}
                              className={TABLE_CELL}
                            >
                              <Box
                                sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}
                              >
                                <MainColumnBlock withPadding>
                                  <Typography title={`Employee group: ${group?.name}`}>{group?.name}</Typography>
                                </MainColumnBlock>
                              </Box>
                            </Grid>

                            {!!groupsDocsByPeriod.length && (
                              <Grid
                                item
                                container
                                wrap="nowrap"
                                sx={{
                                  flexGrow: groupsDocsByPeriod.length,
                                  width: "100%",
                                }}
                                className={PERIOD_CELL_ROW}
                              >
                                {groupsDocsByPeriod.map(
                                  ({
                                    id,
                                    documents,
                                    periodType,
                                    periodYear,
                                    periodNumber,
                                    deadline,
                                    transactionsAmount,
                                    currency,
                                    lastAddedDate,
                                    offset,
                                  }) => {
                                    return (
                                      <React.Fragment key={idx}>
                                        <Grid
                                          item
                                          xs
                                          sx={{ flexGrow: 1 }}
                                          className={TABLE_CELL}
                                        >
                                          <Box
                                            sx={{
                                              width: "100%",
                                              height: "100%",
                                              minHeight: "100px",
                                              display: "flex",
                                              flexDirection: "column",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              overflow: "hidden",
                                            }}
                                          >
                                            {transactionsAmount !== 0 && (
                                              <Typography title={currency}>
                                                <u>
                                                  {Number(transactionsAmount).toLocaleCustom()} {currency}
                                                </u>
                                              </Typography>
                                            )}
                                            {documents && documents?.length > 0 && (
                                              <>
                                                <Grid>
                                                  {(() => {
                                                    const numberOfDocsWithoutLegalEntity =
                                                      getNumberOfDocsWithoutLegalEntity(documents);
                                                    if (numberOfDocsWithoutLegalEntity > 0)
                                                      return (
                                                        <Tooltip
                                                          title={`${numberOfDocsWithoutLegalEntity} document without legal entity`}
                                                        >
                                                          <Grid>
                                                            <FontAwesomeIcon
                                                              icon={faExclamationCircle}
                                                              style={{ color: theme.palette.error.main }}
                                                            />
                                                          </Grid>
                                                        </Tooltip>
                                                      );
                                                  })()}
                                                </Grid>
                                                <AttachedDocuments
                                                  documentIDs={documents.map(({ id }) => id)}
                                                  count={documents.length}
                                                  hasAllVerifiedDocuments={hasAllVerifiedDocuments(documents)}
                                                  hasAllProcessedDocuments={hasAllProcessedDocuments(documents)}
                                                  handleNotifyParent={handleNotifyTable}
                                                  buttonSize="2x"
                                                  documentsNames={documents?.map((document) => document?.name)}
                                                  documentsReadOnlyFields={documents.map((document) => {
                                                    return {
                                                      documentID: document?.id,
                                                      readOnlyFields: {
                                                        isLegalEntityRO: true,
                                                        isCategoryRO: true,
                                                        isTypeRO: true,
                                                        isEmployeeRO: true,
                                                        isDateRO: true,
                                                        isStatusRO: true,
                                                        isPeriodRO: true,
                                                      },
                                                    };
                                                  })}
                                                  useSimpleView
                                                  documentIsReadOnly
                                                />
                                              </>
                                            )}
                                            <DeadlineAndActions
                                              deadlineOverdued={
                                                !!documents &&
                                                (!!deadline ? differenceInDays(new Date(deadline), Date.now()) : 0) < 0
                                              }
                                            >
                                              <Deadline
                                                deadline={deadline}
                                                hasDocuments={documents && !!documents.length ? true : false}
                                                lastAddedDate={lastAddedDate}
                                                periodType={periodType}
                                                offset={offset}
                                              />
                                              <DocReportCellActions
                                                handleNotifyTable={handleNotifyTable}
                                                documentFieldsProps={{
                                                  category: reportCategory.docCategory,
                                                  legalEntity: legalEntity ?? null,
                                                  type: reportCategory?.docType,
                                                  period: Period.makePeriod(periodYear, periodType, periodNumber),
                                                  client: reportCategory.client,
                                                  vendor: reportCategory.vendor,
                                                }}
                                                hasDeadline={!!deadline}
                                              />
                                            </DeadlineAndActions>
                                          </Box>
                                        </Grid>
                                      </React.Fragment>
                                    );
                                  }
                                )}
                              </Grid>
                            )}
                          </Grid>
                        </>
                      )}
                    </>
                  );
                })}
              </React.Fragment>
            );
          })}
        </>
      ) : (
        <>
          <CategoryNameWrapper className={TABLE_BODY_ROW}>
            <CategoryNameContainer>
              <TitleName
                variant="h5"
                title={`${categoryName} (${categoryCode})`}
              >
                {categoryName}
              </TitleName>
              {!reportCategoryGroups?.length && (
                <Typography sx={{ fontStyle: "italic" }}>
                  (No documents available for this document category)
                </Typography>
              )}
            </CategoryNameContainer>
          </CategoryNameWrapper>

          {reportCategoryGroups.map((reportCategory, idx) => (
            <Grid
              key={reportCategory.id}
              className={TABLE_BODY_ROW}
              container
              wrap="nowrap"
              sx={{
                width: "100%",

                [`& .${TABLE_CELL}`]: {
                  borderRight: `1px solid ${darken(COLORS.black_gradients.greyLight, 0.2)}`,
                },

                [`& .${PERIOD_CELL_ROW}`]: {
                  "& div:last-of-type": {
                    borderRight: `0px`,
                  },
                },
              }}
            >
              <Grid
                item
                sx={{ width: "20%", minWidth: "20%", flexGrow: 1 }}
                className={TABLE_CELL}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: "100%",
                    padding: "16px",
                    overflow: "hidden",
                  }}
                >
                  <MainColumnBlock>
                    <Typography title={reportCategory.reportGroupCode}>
                      {reportCategory.reportGroupName +
                        (reportCategory.docType &&
                        reportCategory.docType?.legislation &&
                        reportCategory.docType?.legislation?.id !== ALL_LEGISLATIONS_ID
                          ? " (" + reportCategory.docType?.legislation?.code + ")"
                          : "")}
                    </Typography>
                    <DocTypeActions
                      handleNotifyTable={handleNotifyTable}
                      documentFieldsProps={{
                        category: reportCategory.docCategory,
                        legalEntity,
                        type: reportCategory.docType,
                        period: Period.makePeriod(
                          reportYear ?? new Date().getFullYear(),
                          PeriodType.CalendaristicYear,
                          1
                        ),
                        client: reportCategory.client,
                        vendor: reportCategory.vendor,
                      }}
                      askHide={askHide}
                      askVerified={askVerified}
                      askAction={(action) =>
                        askForAction(action, reportCategory?.docsByPeriod, reportCategory?.docType?.id ?? "")
                      }
                    />
                  </MainColumnBlock>
                </Box>
              </Grid>

              {dynamicColumn &&
                !!reportCategory.invoiceBalance?.length &&
                !!reportCategory.documentsReportPeriodAndCurrency?.length && (
                  <Grid sx={{ width: "10%", overflow: "hidden" }}>
                    {reportCategory.invoiceBalance.map((invoiceBalance) => (
                      <Grid
                        item
                        className={TABLE_CELL}
                        style={{ borderBottom: `1px solid ${lighten(theme.palette.secondary.light, 0.5)}` }}
                      >
                        <Box
                          sx={{
                            height: "100px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}
                        >
                          {!!dynamicColumn.Component ? (
                            <dynamicColumn.Component
                              value={invoiceBalance[dynamicColumn.field as keyof InvoiceBalance] as string}
                              currency={invoiceBalance.currency}
                              client={reportCategory.client}
                              vendor={reportCategory.vendor}
                            />
                          ) : (
                            (reportCategory[dynamicColumn.field as keyof DocumentsReportCategoryGroupDTO] as string)
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}

              {!!dynamicColumn &&
                reportCategory.invoiceBalance.length > 0 &&
                !reportCategory.documentsReportPeriodAndCurrency?.length && (
                  <>
                    <Grid sx={{ width: "10%", overflow: "hidden" }}>
                      {reportCategory.invoiceBalance.map((invoiceBalance) => (
                        <Grid
                          item
                          className={TABLE_CELL}
                          style={{ borderBottom: `1px solid ${lighten(theme.palette.secondary.light, 0.5)}` }}
                        >
                          <Box
                            sx={{
                              height: "100px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                            }}
                          >
                            {!!dynamicColumn.Component ? (
                              <dynamicColumn.Component
                                value={invoiceBalance[dynamicColumn.field as keyof InvoiceBalance] as string}
                                currency={invoiceBalance.currency}
                                client={reportCategory.client}
                                vendor={reportCategory.vendor}
                              />
                            ) : (
                              (reportCategory[dynamicColumn.field as keyof DocumentsReportCategoryGroupDTO] as string)
                            )}
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                    <Grid
                      item
                      container
                      wrap="nowrap"
                      sx={{ height: `${(1 / reportCategory.invoiceBalance.length) * 100}%` }}
                      className={PERIOD_CELL_ROW}
                    ></Grid>
                  </>
                )}

              {!!reportCategory.documentsReportPeriodAndCurrency?.length &&
                (reportCategory.documentsReportPeriodAndCurrency?.length > 0 ||
                  reportCategory.invoiceBalance.length > 0) && (
                  <Grid
                    id="rowcontainer"
                    style={{ display: "flex", flexDirection: "column", width: "100%", height: "auto" }}
                  >
                    {reportCategory.invoiceBalance.map((_invoiceBalance) => {
                      const documentReportAndCurrency = reportCategory.documentsReportPeriodAndCurrency?.find(
                        (_documentReportAndCurrency) =>
                          _documentReportAndCurrency.currency?.id === _invoiceBalance.currency?.id
                      );

                      return documentReportAndCurrency ? (
                        <Grid
                          item
                          container
                          wrap="nowrap"
                          sx={{ height: `${(1 / reportCategory.invoiceBalance.length) * 100}%` }}
                          className={PERIOD_CELL_ROW}
                        >
                          {documentReportAndCurrency.docsByPeriod.map(
                            ({
                              id,
                              documents,
                              periodType,
                              periodYear,
                              periodNumber,
                              deadline,
                              transactionsAmount,
                              currency,
                              lastAddedDate,
                              offset,
                              invoicesPaid,
                              invoicesSentToClient,
                              invoicesReceivedFromVendor,
                            }) => {
                              const invoicesMessage =
                                screen !== ScreenReport.invoiceTracking
                                  ? ""
                                  : !!invoicesPaid
                                  ? "Invoices are paid"
                                  : !invoicesPaid && invoicesSentToClient
                                  ? "Invoices were send to client"
                                  : !invoicesPaid && invoicesReceivedFromVendor
                                  ? "Invoices were received from vendor"
                                  : "";

                              const transactionIds =
                                documents
                                  ?.map(
                                    (doc) =>
                                      doc?.documentTransactions?.map(
                                        (item) => (item.transaction?.id ?? "") as string
                                      ) ?? []
                                  )
                                  ?.reduce((acc, curr) => [...acc, ...curr], []) ?? [];

                              return (
                                <Grid
                                  key={id}
                                  className={TABLE_CELL}
                                  item
                                  xs
                                  style={{ borderBottom: `1px solid ${lighten(theme.palette.secondary.light, 0.5)}` }}
                                  sx={{
                                    flexGrow: 1,
                                    overflow: "hidden",
                                    ...(screen === ScreenReport.invoiceTracking &&
                                      (!!invoicesPaid ||
                                        (documents?.length === 0 &&
                                          (periodYear < TODAY.getFullYear() ||
                                            (periodType === "M" &&
                                              periodYear === TODAY.getFullYear() &&
                                              periodNumber < TODAY.getMonth() + 1)))) && {
                                        background: lighten(theme.palette.success.light, 0.6),
                                      }),

                                    ...(screen === ScreenReport.invoiceTracking &&
                                      !invoicesPaid &&
                                      (invoicesSentToClient || invoicesReceivedFromVendor) && {
                                        background: lighten(theme.palette.success.main, 0.9),
                                      }),
                                  }}
                                  title={
                                    screen === ScreenReport.invoiceTracking && !!invoicesMessage
                                      ? invoicesMessage
                                      : undefined
                                  }
                                >
                                  <CellBlock>
                                    {transactionsAmount !== 0 && (
                                      <>
                                        {!AmountDetails && (
                                          <Typography title={documentReportAndCurrency.currency?.code}>
                                            <u>
                                              {Number(transactionsAmount).toLocaleCustom()}{" "}
                                              {documentReportAndCurrency.currency?.code}
                                            </u>
                                          </Typography>
                                        )}

                                        {AmountDetails && (
                                          <AmountDetails
                                            client={reportCategory.client}
                                            vendor={reportCategory.vendor}
                                            totalAmount={`${Number(transactionsAmount).toLocaleCustom()} ${
                                              documentReportAndCurrency.currency?.code
                                            }`}
                                            transactionIds={transactionIds}
                                          >
                                            <Typography title={reportCategory.currency?.code}>
                                              <u>
                                                {Number(transactionsAmount).toLocaleCustom()}{" "}
                                                {documentReportAndCurrency.currency?.code}
                                              </u>
                                            </Typography>
                                          </AmountDetails>
                                        )}
                                      </>
                                    )}
                                    {documents && documents?.length > 0 && (
                                      <>
                                        <Grid>
                                          {(() => {
                                            const numberOfDocsWithoutLegalEntity =
                                              getNumberOfDocsWithoutLegalEntity(documents);
                                            if (numberOfDocsWithoutLegalEntity > 0)
                                              return (
                                                <Tooltip
                                                  title={`${numberOfDocsWithoutLegalEntity} document without legal entity`}
                                                >
                                                  <Grid>
                                                    <FontAwesomeIcon
                                                      icon={faExclamationCircle}
                                                      style={{ color: theme.palette.error.main }}
                                                    />
                                                  </Grid>
                                                </Tooltip>
                                              );
                                          })()}
                                        </Grid>
                                        <AttachedDocuments
                                          documentIDs={documents.map(({ id }) => id)}
                                          count={documents.length}
                                          hasAllVerifiedDocuments={hasAllVerifiedDocuments(documents)}
                                          hasAllProcessedDocuments={hasAllProcessedDocuments(documents)}
                                          handleNotifyParent={handleNotifyTable}
                                          buttonSize="2x"
                                          documentsNames={documents?.map((document) => document?.name)}
                                          useSimpleView
                                        />
                                      </>
                                    )}
                                    <DeadlineAndActions
                                      deadlineOverdued={
                                        !!documents &&
                                        (!!deadline ? differenceInDays(new Date(deadline), Date.now()) : 0) < 0
                                      }
                                    >
                                      <Deadline
                                        deadline={deadline}
                                        hasDocuments={documents && !!documents.length ? true : false}
                                        lastAddedDate={lastAddedDate}
                                        periodType={periodType}
                                        offset={offset}
                                      />
                                      <DocReportCellActions
                                        handleNotifyTable={handleNotifyTable}
                                        documentFieldsProps={{
                                          category: reportCategory.docCategory,
                                          legalEntity: legalEntity ?? null,
                                          type: reportCategory?.docType,
                                          period: Period.makePeriod(periodYear, periodType, periodNumber),
                                          client: reportCategory.client,
                                          vendor: reportCategory.vendor,
                                        }}
                                        hasDeadline={!!deadline}
                                      />
                                    </DeadlineAndActions>
                                  </CellBlock>
                                </Grid>
                              );
                            }
                          )}
                        </Grid>
                      ) : (
                        <>
                          <Grid
                            item
                            container
                            wrap="nowrap"
                            sx={{ height: `${(1 / reportCategory.invoiceBalance.length) * 100}%` }}
                            className={PERIOD_CELL_ROW}
                          >
                            {reportCategory.documentsReportPeriodAndCurrency?.length &&
                              reportCategory.documentsReportPeriodAndCurrency[0].docsByPeriod.map(
                                (_docsByPeriod, _idx) => (
                                  <Grid
                                    key={_idx}
                                    className={TABLE_CELL}
                                    item
                                    xs
                                    style={{ borderBottom: `1px solid ${lighten(theme.palette.secondary.light, 0.5)}` }}
                                    sx={{
                                      flexGrow: 1,
                                      overflow: "hidden",
                                    }}
                                  >
                                    <CellBlock>
                                      <DeadlineAndActions deadlineOverdued={false}>
                                        <DocReportCellActions
                                          handleNotifyTable={handleNotifyTable}
                                          documentFieldsProps={{
                                            category: reportCategory.docCategory,
                                            legalEntity: legalEntity ?? null,
                                            type: reportCategory?.docType,
                                            period: Period.makePeriod(
                                              _docsByPeriod.periodYear,
                                              _docsByPeriod.periodType,
                                              _docsByPeriod.periodNumber
                                            ),
                                            client: reportCategory.client,
                                            vendor: reportCategory.vendor,
                                          }}
                                        />
                                      </DeadlineAndActions>
                                    </CellBlock>
                                  </Grid>
                                )
                              )}
                          </Grid>
                        </>
                      );
                    })}
                  </Grid>
                )}

              {!!reportCategory.docsByPeriod?.length && (
                <Grid
                  id="rowcontainer"
                  style={{ display: "flex", width: "100%" }}
                >
                  <Grid
                    item
                    container
                    wrap="nowrap"
                    sx={{ flexGrow: reportCategory.docsByPeriod.length }}
                    className={PERIOD_CELL_ROW}
                  >
                    {reportCategory.docsByPeriod.map(
                      ({
                        id,
                        documents,
                        periodType,
                        periodYear,
                        periodNumber,
                        deadline,
                        transactionsAmount,
                        currency,
                        lastAddedDate,
                        offset,
                        invoicesPaid,
                        invoicesSentToClient,
                        invoicesReceivedFromVendor,
                      }) => {
                        const invoicesMessage =
                          screen !== ScreenReport.invoiceTracking
                            ? ""
                            : !!invoicesPaid
                            ? "Invoices are paid"
                            : !invoicesPaid && invoicesSentToClient
                            ? "Invoices were send to client"
                            : !invoicesPaid && invoicesReceivedFromVendor
                            ? "Invoices were received from vendor"
                            : "";

                        const transactionIds =
                          documents
                            ?.map(
                              (doc) =>
                                doc?.documentTransactions?.map((item) => (item.transaction?.id ?? "") as string) ?? []
                            )
                            ?.reduce((acc, curr) => [...acc, ...curr], []) ?? [];

                        return (
                          <Grid
                            key={id}
                            className={TABLE_CELL}
                            item
                            xs
                            sx={{
                              flexGrow: 1,
                              overflow: "hidden",
                              ...(screen === ScreenReport.invoiceTracking &&
                                (!!invoicesPaid ||
                                  (documents?.length === 0 &&
                                    (periodYear < TODAY.getFullYear() ||
                                      (periodType === "M" &&
                                        periodYear === TODAY.getFullYear() &&
                                        periodNumber < TODAY.getMonth() + 1)))) && {
                                  background: lighten(theme.palette.success.light, 0.6),
                                }),

                              ...(screen === ScreenReport.invoiceTracking &&
                                !invoicesPaid &&
                                (invoicesSentToClient || invoicesReceivedFromVendor) && {
                                  background: lighten(theme.palette.success.main, 0.9),
                                }),
                            }}
                            title={
                              screen === ScreenReport.invoiceTracking && !!invoicesMessage ? invoicesMessage : undefined
                            }
                          >
                            <CellBlock>
                              {transactionsAmount !== 0 && (
                                <>
                                  {!AmountDetails && (
                                    <Typography title={currency}>
                                      <u>
                                        {Number(transactionsAmount).toLocaleCustom()} {currency}
                                      </u>
                                    </Typography>
                                  )}

                                  {AmountDetails && (
                                    <AmountDetails
                                      client={reportCategory.client}
                                      vendor={reportCategory.vendor}
                                      totalAmount={`${Number(transactionsAmount).toLocaleCustom()} ${currency}`}
                                      transactionIds={transactionIds}
                                    >
                                      <Typography title={currency}>
                                        <u>
                                          {Number(transactionsAmount).toLocaleCustom()} {currency}
                                        </u>
                                      </Typography>
                                    </AmountDetails>
                                  )}
                                </>
                              )}
                              {documents && documents?.length > 0 && (
                                <>
                                  <Grid>
                                    {(() => {
                                      const numberOfDocsWithoutLegalEntity =
                                        getNumberOfDocsWithoutLegalEntity(documents);
                                      if (numberOfDocsWithoutLegalEntity > 0)
                                        return (
                                          <Tooltip
                                            title={`${numberOfDocsWithoutLegalEntity} document without legal entity`}
                                          >
                                            <Grid>
                                              <FontAwesomeIcon
                                                icon={faExclamationCircle}
                                                style={{ color: theme.palette.error.main }}
                                              />
                                            </Grid>
                                          </Tooltip>
                                        );
                                    })()}
                                  </Grid>
                                  <AttachedDocuments
                                    documentIDs={documents.map(({ id }) => id)}
                                    count={documents.length}
                                    hasAllVerifiedDocuments={hasAllVerifiedDocuments(documents)}
                                    hasAllProcessedDocuments={hasAllProcessedDocuments(documents)}
                                    handleNotifyParent={handleNotifyTable}
                                    buttonSize="2x"
                                    documentsNames={documents?.map((document) => document?.name)}
                                    useSimpleView
                                  />
                                </>
                              )}
                              <DeadlineAndActions
                                deadlineOverdued={
                                  !!documents && (!!deadline ? differenceInDays(new Date(deadline), Date.now()) : 0) < 0
                                }
                              >
                                <Deadline
                                  deadline={deadline}
                                  hasDocuments={documents && !!documents.length ? true : false}
                                  lastAddedDate={lastAddedDate}
                                  periodType={periodType}
                                  offset={offset}
                                />
                                <DocReportCellActions
                                  handleNotifyTable={handleNotifyTable}
                                  documentFieldsProps={{
                                    category: reportCategory.docCategory,
                                    legalEntity: legalEntity ?? null,
                                    type: reportCategory?.docType,
                                    period: Period.makePeriod(periodYear, periodType, periodNumber),
                                    client: reportCategory.client,
                                    vendor: reportCategory.vendor,
                                  }}
                                  hasDeadline={!!deadline}
                                />
                              </DeadlineAndActions>
                            </CellBlock>
                          </Grid>
                        );
                      }
                    )}
                  </Grid>
                </Grid>
              )}
            </Grid>
          ))}
        </>
      )}
    </>
  );
};

export default DocumentsReportCategory;
